import { i18n } from '@/lib'
import { parseDateOnly, toDateOnly } from '@/lib/date'

import { isAfter, isBefore } from 'date-fns'

interface ValidationOptions {
  min?: number | string
  max?: number | string
  regex?: string | RegExp
  characterClasses?: number
  required?: boolean
}

type CharacterClass = 'lowercase' | 'uppercase' | 'number' | 'symbol'

const characterClassPatterns: Record<CharacterClass, RegExp> = {
  lowercase: /[a-z]/g,
  uppercase: /[A-Z]/g,
  number: /[0-9]/g,
  symbol: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/g,
}

function validateCharacterClasses(value: string, requiredCount: number): boolean {
  if (!requiredCount) return true

  // Count how many different character classes are used in the input
  let classesUsed = 0

  if (/[a-z]/.test(value)) classesUsed++
  if (/[A-Z]/.test(value)) classesUsed++
  if (/[0-9]/.test(value)) classesUsed++
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value)) classesUsed++

  return classesUsed >= requiredCount
}

/**
 * Validates a string value against a set of validation rules
 * @param value The string value to validate
 * @param options Validation options including min/max length, regex pattern, and character class count
 * @returns true if validation passes, localized error message if validation fails
 */
export function handleStringValidation(value: string, options: ValidationOptions = {}) {
  // Treat empty string as required unless explicitly marked optional
  if (!value) return options.required === false ? true : i18n('Field is required')
  // Validate minimum length
  else if (options.min && value && value.length < Number(options.min)) {
    return i18n('Length must be at least $').replace('$', options.min.toString())
  }

  // Validate maximum length
  else if (options.max && value && value.length > Number(options.max)) {
    return i18n('Length must be at most $').replace('$', options.max.toString())
  }

  // Validate character classes if specified
  else if (
    options.characterClasses &&
    value &&
    !validateCharacterClasses(value, options.characterClasses)
  ) {
    return i18n(
      'Must contain at least $ special characters (lowercase, uppercase, numbers, or symbols)',
    ).replace('$', options.characterClasses.toString())
  }

  // Validate against regex pattern if provided
  else if (options.regex && value) {
    const regexPattern = options.regex instanceof RegExp ? options.regex : new RegExp(options.regex)
    if (!regexPattern.test(value)) return i18n('Format is invalid')
  }

  return true
}

/**
 * Validates a numeric string value against a set of validation rules
 * @param value The string value to validate as a number
 * @param options Validation options including min/max values and regex pattern
 * @returns true if validation passes, localized error message if validation fails
 */
export function handleNumberValidation(
  value: string,
  options: ValidationOptions = {},
): true | string {
  const numberValue = Number(value)

  if (Number.isNaN(numberValue)) return i18n('Value must be a number')
  if (!Number.isInteger(numberValue)) return i18n('Value must be an integer')
  if (numberValue < 0) return i18n('Value must be positive')
  if (options.min != null && numberValue < Number(options.min))
    return i18n('Value must be at least $').replace('$', String(options.min))
  if (options.max != null && numberValue > Number(options.max))
    return i18n('Value must be at most $').replace('$', String(options.max))
  if (options.regex && !new RegExp(options.regex).test(value)) return i18n('Format is invalid')

  return true
}

/**
 * Validates a boolean value, ensuring it is explicitly true or false
 * @param value The boolean value to validate
 * @returns true if validation passes, localized error message if validation fails
 */
export function handleBooleanValidation(value: boolean | undefined | null): true | string {
  return value === true || value === false ? true : i18n('Field is required')
}

/**
 * Validates an array value against validation rules
 * @param value The value to validate as an array
 * @param options Validation options including min/max length
 * @returns true if validation passes, localized error message if validation fails
 */
export function handleArrayValidation(
  value: unknown,
  options: ValidationOptions = {},
): true | string {
  // Check if value is null/undefined
  if (value == null) return i18n('Field is required')

  // Ensure value is an array
  if (!Array.isArray(value)) return i18n('Value must be an array')

  // Check minimum length if specified
  if (options.min != null && value.length < Number(options.min))
    return i18n('Must select at least $ items').replace('$', String(options.min))

  // Check maximum length if specified
  if (options.max != null && value.length > Number(options.max))
    return i18n('Cannot select more than $ items').replace('$', String(options.max))

  // Empty array check
  if (value.length === 0) return i18n('Field is required')

  return true
}

/**
 * Validates a date string against validation rules
 * @param value The date string to validate (date-only YYYY-MM-DD or ISO format) or null
 * @param options Validation options including min/max dates
 * @returns true if validation passes, localized error message if validation fails
 */
export function handleDateValidation(
  value: string | null,
  options: ValidationOptions = {},
): true | string {
  // Check for null/empty value
  if (!value) return i18n('Field is required')

  // Use date-only-safe parser to avoid timezone drift
  const dateValue = parseDateOnly(value)
  if (Number.isNaN(dateValue.getTime())) {
    return i18n('Format is invalid')
  }

  // Validate minimum date if specified
  if (options.min) {
    const minDate = parseDateOnly(String(options.min))
    if (!Number.isNaN(minDate.getTime()) && isBefore(dateValue, minDate)) {
      const formattedMin = toDateOnly(minDate)
      return i18n('Date must be at least $').replace('$', formattedMin)
    }
  }

  // Validate maximum date if specified
  if (options.max) {
    const maxDate = parseDateOnly(String(options.max))
    if (!Number.isNaN(maxDate.getTime()) && isAfter(dateValue, maxDate)) {
      const formattedMax = toDateOnly(maxDate)
      return i18n('Date must be at most $').replace('$', formattedMax)
    }
  }

  return true
}
